/**
 * Die Karte „Für Rauschen gehalten“ im Chat. (19.10.2026, Journal 08:1x: `rauschen-verworfen zeichen=15 weil=auto-mikro`)
 *
 * Der Rauschfilter des Servers (server/schmied-rauschen.ts) verwirft kurze Texte im Faden zum Schmied. Das bleibt so — aber nie
 * still: die Karte zeigt Martin, WAS erkannt wurde, und gibt ihm zwei große Knöpfe: „Trotzdem senden“ und „Verwerfen“.
 *
 * ⚠️ Der Filter wird nicht gelockert: ohne Martins Tipp geht nichts hinaus. Sein Tipp ist das ausdrückliche Ja; er zählt nur bei einem
 *    ECHTEN Tipp (`isTrusted`, wie beim Ja-Knopf der Schmied-Fragen, lib/frage-knoepfe.ts) und sendet über denselben Sendeweg mit
 *    Empfänger „Schmied“ (wie dort) — so, als hätte Martin den Text gesprochen und den Empfänger gewählt.
 * ⚠️ Der Text steht NUR in dieser Karte (textContent). Journal: nur Zahlen und Wörter (`zeichen`, `knopf=gesendet|verworfen`).
 * ⚠️ Die Karte verschwindet nicht von selbst vor RAUSCH_KARTE_MAX_MS (10 Minuten, weit über 60 s); eine neuere ersetzt sie.
 * ⚠️ Doppeltipp-sicher; scheitert das Senden, gehen die Knöpfe wieder auf. Der Hinweis-Ton ist abschaltbar (Schalter in der Karte,
 *    gemerkt im Gerät); ohne Speicher gilt „an“.
 */

export const RAUSCH_KARTE_KLASSE = "rausch-karte";
/** So lange bleibt die Karte höchstens stehen (Vorgabe: nicht vor 60 s). */
export const RAUSCH_KARTE_MAX_MS = 10 * 60_000;
export const RAUSCH_TON_SCHLUESSEL = "rauschKarteTon";
/** Der Hinweis-Ton klingt höchstens einmal in dieser Zeit. */
export const RAUSCH_TON_ABSTAND_MS = 60_000;
let letzterTonMs = -Infinity;
/** Nur für Tests: die Drossel zurücksetzen. */
export function rauschTonDrosselZuruecksetzen(): void {
  letzterTonMs = -Infinity;
}

export type RauschKnopf = "gesendet" | "verworfen";
/** Sendet den Text als Martins Nachricht an den Schmied; `false` = nicht angekommen. */
export type RauschSender = (text: string) => Promise<boolean> | boolean;
/** Journal-Haken: nur Zahlen und Wörter, nie Text. */
export type RauschMelder = (knopf: RauschKnopf, zeichen: number) => void;

export interface RauschKarteUmgebung {
  sende: RauschSender;
  melde?: RauschMelder;
  /** Der Hinweis-Ton (nur gespielt, wenn nicht abgeschaltet). */
  ton?: () => void;
  /** Die Uhr (Tests). */
  jetzt?: () => number;
  /** Wird gerufen, wenn die Karte geht (Knopf oder Ablauf). */
  beiEnde?: () => void;
}

function holeSpeicher(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Ist der Hinweis-Ton an? Kaputter oder fehlender Speicher: an. */
export function rauschTonAn(speicher: Storage | null = holeSpeicher()): boolean {
  try {
    return speicher?.getItem(RAUSCH_TON_SCHLUESSEL) !== "aus";
  } catch {
    return true;
  }
}

function setzeTon(an: boolean, speicher: Storage | null = holeSpeicher()): void {
  try {
    speicher?.setItem(RAUSCH_TON_SCHLUESSEL, an ? "an" : "aus");
  } catch {
    /* ohne Speicher gilt „an“ */
  }
}

/** Eine Karte (noch nicht eingehängt). */
export function baueRauschKarte(doc: Document, daten: { text: string; hinweis: string }, umg: RauschKarteUmgebung): HTMLElement {
  const gehoert = daten.text.trim();
  const zeichen = daten.text.length;
  const karte = doc.createElement("section");
  karte.className = RAUSCH_KARTE_KLASSE;
  karte.setAttribute("aria-label", "Etwas gehört, aber für Rauschen gehalten");

  const titel = doc.createElement("h3");
  titel.className = "rausch-titel";
  titel.textContent = "Das habe ich gehört – nicht gesendet";
  karte.appendChild(titel);

  const text = doc.createElement("p");
  text.className = "rausch-text";
  text.textContent = gehoert ? `„${gehoert}“` : "(kein Wort erkannt)";
  karte.appendChild(text);

  const status = doc.createElement("p");
  status.className = "rausch-status";
  status.setAttribute("role", "status");
  status.textContent = daten.hinweis || "Ich habe es für Rauschen gehalten.";
  karte.appendChild(status);

  const knoepfe = doc.createElement("div");
  knoepfe.className = "rausch-knoepfe";
  knoepfe.setAttribute("role", "group");
  knoepfe.setAttribute("aria-label", "Was soll mit dem Gehörten geschehen?");
  const alle = (): HTMLButtonElement[] => Array.from(knoepfe.querySelectorAll("button"));
  const entferne = (): void => {
    karte.remove();
    umg.beiEnde?.();
  };
  const oeffneWieder = (): void => {
    delete karte.dataset.sendet;
    for (const b of alle()) b.disabled = false;
    status.textContent = "Das hat nicht geklappt. Versuch es noch einmal.";
  };
  const mach = (beschriftung: string, art: "senden" | "verwerfen", aktion: () => void): HTMLButtonElement => {
    const k = doc.createElement("button");
    k.type = "button";
    k.className = `rausch-knopf rausch-knopf-${art}`;
    k.textContent = beschriftung;
    k.addEventListener("click", (ev) => {
      if (!ev.isTrusted) return; // künstliches Ereignis (Skript, Fremdtext): ignorieren
      if (karte.dataset.sendet === "1") return;
      karte.dataset.sendet = "1";
      for (const b of alle()) b.disabled = true;
      aktion();
    });
    return k;
  };
  const senden = mach("Trotzdem senden", "senden", () => {
    status.textContent = "wird gesendet …";
    void Promise.resolve(umg.sende(gehoert)).then(
      (ok) => {
        if (!ok) return oeffneWieder();
        umg.melde?.("gesendet", zeichen);
        entferne();
      },
      () => oeffneWieder(),
    );
  });
  // Ohne erkannten Text gibt es nichts zu senden.
  if (!gehoert) senden.hidden = true;
  const verwerfen = mach("Verwerfen", "verwerfen", () => {
    umg.melde?.("verworfen", zeichen);
    entferne();
  });
  knoepfe.append(senden, verwerfen);
  karte.appendChild(knoepfe);

  const tonZeile = doc.createElement("label");
  tonZeile.className = "rausch-ton";
  const haken = doc.createElement("input");
  haken.type = "checkbox";
  haken.checked = rauschTonAn();
  haken.addEventListener("change", () => setzeTon(haken.checked));
  tonZeile.append(haken, doc.createTextNode(" Hinweis-Ton"));
  karte.appendChild(tonZeile);
  return karte;
}

/** Zeigt die Karte am Ende des Verlaufs (eine ältere wird ersetzt), spielt den Ton und räumt nach dem Höchstalter auf. */
export function zeigeRauschKarte(wurzel: HTMLElement, daten: { text: string; hinweis: string }, umg: RauschKarteUmgebung): HTMLElement {
  for (const alt of Array.from(wurzel.querySelectorAll(`:scope > .${RAUSCH_KARTE_KLASSE}`))) alt.remove();
  const karte = baueRauschKarte(wurzel.ownerDocument, daten, umg);
  wurzel.appendChild(karte);
  // Der Ton höchstens einmal je Minute (die Karte selbst erscheint immer).
  const jetzt = umg.jetzt?.() ?? Date.now();
  if (rauschTonAn() && jetzt - letzterTonMs >= RAUSCH_TON_ABSTAND_MS) {
    letzterTonMs = jetzt;
    try {
      umg.ton?.();
    } catch {
      /* der Ton ist nur ein Hinweis */
    }
  }
  const frist = setTimeout(() => {
    if (!karte.isConnected) return;
    karte.remove();
    umg.beiEnde?.();
  }, RAUSCH_KARTE_MAX_MS);
  (frist as { unref?: () => void }).unref?.();
  return karte;
}
